import { useEffect, useMemo, useState } from "react";
import { observer } from "mobx-react-lite";
import { useQuery } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { useLanguage } from "@/lib/i18n/LanguageContext";
import { useOrganizationContext } from "@/lib/context/OrganizationContext";
import { useOrganizationStore } from "@/hooks/useOrganizationStore";
import { usePermissions } from "@/hooks/usePermissions";
import { ROLES, type AppRole } from "@/constants/permissions";
import { useRoleLabel } from "@/hooks/useRoleLabel";
import type { OrgFolderOption, OrgProjectOption } from "@/stores/organizations/repository";
import {
  buildFolderIndex,
  descendantIds,
  maximalExclusions,
  toggleFolderExclusion,
  type FolderIndex,
} from "@/components/settings/ProjectFolderPicker";
import { MemberAccessEditor } from "@/components/settings/MemberAccessEditor";
import { MEMBER_ACCESS_PANE_HEIGHT } from "@/components/settings/memberAccessLayout";
import { cn } from "@/lib/utils";
import {
  buildProjectPermissionsPayload,
  presetPermissions,
  samePermissions,
  type ConfigurablePermission,
  type PermissionCatalog,
  type ProjectPermissionSet,
  type ProjectPermissionsPayload,
} from "@/lib/memberProjectPermissions";
import { splitAssignments } from "@/components/settings/subcontractorAssignments";
import { Loader2, Mail } from "lucide-react";
import { track } from "@/lib/posthog";

interface InviteUserDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onInviteSent?: () => void;
}

/** The offer list plus the folders that can be denied inside it. */
interface InviteProjectsData {
  projects: OrgProjectOption[];
  folders: OrgFolderOption[];
  /** The per-project permission catalogue; null when the database predates it. */
  catalog: PermissionCatalog | null;
}

/**
 * Default to subcontractor (the common invite) when available, else the first
 * role the actor is allowed to assign.
 */
function defaultInviteRole(assignableMemberRoles: AppRole[]): AppRole | "" {
  return assignableMemberRoles.includes(ROLES.SUBCONTRACTOR)
    ? ROLES.SUBCONTRACTOR
    : (assignableMemberRoles[0] ?? "");
}

/**
 * Load the org's projects, and the folders inside them, for a subcontractor
 * invite.
 *
 * Keyed by org id, so switching orgs can never render the previous org's
 * projects — this dialog is mounted for the life of the settings page and org
 * switching never unmounts it. `staleTime: 0` means re-enabling on open refetches
 * too, so a project archived meanwhile stops being offered. A failed load is NOT
 * an empty org: rendering the "no projects yet" copy would tell the admin to go
 * create a project that already exists, so the error state stays distinct and
 * retryable. Both reads share the key so the ticks and the folder tree under
 * them always come from one fetch.
 */
function useInviteProjects(enabled: boolean, orgId: string | undefined) {
  const organizationStore = useOrganizationStore();

  return useQuery<InviteProjectsData>({
    queryKey: ["invite-org-projects", orgId],
    queryFn: async () => {
      if (!orgId) throw new Error("Missing organisation");
      const [projects, folders, catalog] = await Promise.all([
        organizationStore.fetchOrgProjects(orgId),
        organizationStore.fetchOrgProjectFolders(orgId),
        organizationStore.fetchProjectPermissionCatalog(),
      ]);
      return { projects, folders, catalog };
    },
    enabled: enabled && !!orgId,
    staleTime: 0,
    refetchOnMount: "always",
  });
}

interface InviteProjectPickerProps {
  /** `undefined` is "never loaded", which is not the same as "loaded, empty". */
  data: InviteProjectsData | undefined;
  selectedProjectIds: Set<number>;
  excludedFolderIds: Set<number>;
  onToggle: (projectId: number) => void;
  onToggleFolder: (folderId: number) => void;
  /** Codes per ticked project the admin has set; absent = the Member default. */
  projectPermissions: ReadonlyMap<number, ProjectPermissionSet>;
  onChangePermissions: (projectId: number, next: Set<ConfigurablePermission>) => void;
  isLoading: boolean;
  isError: boolean;
  onRetry: () => void;
  isRetrying: boolean;
  disabled: boolean;
}

function ProjectsLoadFailed({
  message,
  onRetry,
  disabled,
}: {
  message: string;
  onRetry: () => void;
  disabled: boolean;
}) {
  const { t } = useLanguage();

  return (
    <div className="space-y-2">
      <p className="text-sm text-destructive">{message}</p>
      <Button type="button" variant="outline" size="sm" onClick={onRetry} disabled={disabled}>
        {t.common.retry}
      </Button>
    </div>
  );
}

/** The loading / failed / stale / empty / grouped-checkbox states of the invite picker. */
function InviteProjectPicker(props: InviteProjectPickerProps) {
  const { t } = useLanguage();
  const retryDisabled = props.disabled || props.isRetrying;

  if (props.isLoading) {
    return (
      <div className="flex items-center gap-2 text-sm text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" /> {t.settings.roles.loadingProjects}
      </div>
    );
  }

  if (props.isError && !props.data) {
    return (
      <ProjectsLoadFailed
        message={t.settings.roles.projectsLoadFailed}
        onRetry={props.onRetry}
        disabled={retryDisabled}
      />
    );
  }

  return (
    <div className="space-y-2">
      {/* A failed *refetch* keeps the previous list on screen, so without this the
          admin sees rows that look current, a Send button that has silently gone
          disabled, and no way to ask for the list again. */}
      {props.isError && (
        <ProjectsLoadFailed
          message={t.settings.roles.projectsRefreshFailed}
          onRetry={props.onRetry}
          disabled={retryDisabled}
        />
      )}
      <MemberAccessEditor
        projects={props.data?.projects ?? []}
        folders={props.data?.folders ?? []}
        selection={{
          selectedProjectIds: props.selectedProjectIds,
          excludedFolderIds: props.excludedFolderIds,
          onToggleProject: props.onToggle,
          onToggleFolder: props.onToggleFolder,
        }}
        permissions={
          props.data?.catalog
            ? {
                catalog: props.data.catalog,
                byProject: props.projectPermissions,
                onChange: props.onChangePermissions,
              }
            : null
        }
        permissionsNotice={t.settings.memberAccess.permissionsUnavailable}
        emptyMessage={t.settings.roles.noProjectsForInvite}
        disabled={props.disabled}
        paneHeight={MEMBER_ACCESS_PANE_HEIGHT.invite}
      />
    </div>
  );
}

/** Either the scope to invite with, or the message to show instead. */
type InviteProjectSelection =
  | {
      projectIds: number[];
      excludedFolderIds: number[];
      includedFolderIds: number[];
      projectPermissions?: ProjectPermissionsPayload;
    }
  | { error: string; prunedIds?: number[] };

/**
 * Judge the picker's ticks against the offer list this attempt fetched.
 *
 * "No offer list" is not "an empty offer list". The query is disabled while the
 * role is anything else and its cache is dropped ~5 minutes later, so toggling
 * the role away and back leaves the ticks in state with `data` still undefined
 * while the refetch runs. Pruning against nothing there would clear every tick
 * and report projects as withdrawn that are still perfectly grantable — so wait
 * for the list instead. A list left over from before a failed refresh is not a
 * list either: it may still offer the project the refresh was about to drop.
 *
 * A list with a refetch in flight over it is superseded for the same reason, so
 * `isFetching` is read here as well as in the Send button's `offerListReady`:
 * the button keeps a click from starting at all, but this function is the guard
 * that travels with the decision, and the two layers disagreeing is how a later
 * caller ends up judging ticks against a generation already being replaced.
 *
 * What survives is only what the picker still offers. A background refetch can
 * drop a project from the list — its building archived meanwhile — while the
 * tick stays in state, and inviting into it would grant exactly the access the
 * offer list exists to withhold.
 *
 * This is the dialog narrowing what it sends, and nothing more: the archived /
 * hidden / archived-building filter is not re-derived anywhere server-side (the
 * send-organization-invite function and the user_project_assignments RLS live
 * outside this repo and gate on org role only), so it is offer hygiene, not an
 * access boundary. Whoever can reach this dialog can already unarchive the
 * building and grant the same access outright.
 *
 * The folder denials are pruned to the projects that survive. Both dialogs prune
 * folders off a project they are no longer granting, for different reasons:
 * there a revoke would cascade the exclusion away by FK, here
 * send-organization-invite validates every excluded folder against the invite's
 * projectIds and rejects the whole invite otherwise.
 *
 * What is left is then reduced to the branches an admin actually unticked
 * (maximalExclusions), which is all the acceptance needs — it expands each id
 * through folder_subtree_ids(). The closed set would put a row per descendant on
 * the wire, and that validation is one `.in("id", …)`: past PostgREST's 1000-row
 * cap it comes back short and rejects a perfectly valid invite, and past the URL
 * length it does not come back at all. The reduction runs AFTER the project
 * prune, so a folder whose only denied ancestor sits in a project this invite
 * does not carry is still sent in its own right.
 */
function resolveInviteProjectIds(
  projectsQuery: { data: InviteProjectsData | undefined; isError: boolean; isFetching: boolean },
  folderIndex: FolderIndex,
  selectedProjectIds: Set<number>,
  excludedFolderIds: Set<number>,
  projectPermissions: ReadonlyMap<number, ProjectPermissionSet>,
  t: ReturnType<typeof useLanguage>["t"]
): InviteProjectSelection {
  if (projectsQuery.isError) return { error: t.settings.roles.projectsRefreshFailed };

  const offered = projectsQuery.data;
  if (!offered || projectsQuery.isFetching) return { error: t.settings.roles.projectsNotLoaded };

  const { visible: projectIds, hidden } = splitAssignments(
    offered.projects.map((project) => project.id),
    Array.from(selectedProjectIds)
  );
  if (hidden.length > 0) {
    // Sending the reduced set silently would report "Invitation sent!" for a
    // narrower grant than the admin made, and they would never learn which
    // project was left out. Drop the dead ticks and make them confirm the
    // remainder.
    return {
      error:
        hidden.length === 1
          ? t.settings.roles.projectNoLongerAvailable
          : t.settings.roles.projectsNoLongerAvailable.replace("{count}", String(hidden.length)),
      prunedIds: projectIds,
    };
  }
  if (projectIds.length === 0) return { error: t.settings.roles.selectAtLeastOneProject };

  const invited = new Set(projectIds);
  const denied = offered.folders
    .filter((folder) => excludedFolderIds.has(folder.id) && invited.has(folder.projectId))
    .map((folder) => folder.id);
  // The server expands each denied folder over its own subtree, so send the
  // shallowest covering roots rather than the closed set the picker draws — the
  // client's tree follows parent_id within a project, the server's also follows
  // parent_document_id, and the server's is the authoritative one.
  //
  // The stop-set travels WITH the invite because acceptance cannot recover it.
  // The manage dialog's RPC derives it — under an end state, a folder the
  // payload does not name is one the admin left ticked. An invite is not an end
  // state: it is applied later, against a tree that has moved on, where a folder
  // missing from the deny-list is missing either because the admin kept it or
  // because it did not exist yet, and those are indistinguishable by then.
  // Naming what was kept AT SEND TIME keeps those apart: folders created or
  // moved under a denied one afterwards are not in it, so they inherit the
  // denial from their parent the way the trigger already gives them.
  //
  // "Kept" is measured against the PICKER's set, not against `denied`. `denied`
  // is already pruned to the invited projects, so a folder the admin unticked in
  // an uninvited project is missing from it — and putting that folder in the
  // stop-set would halt the acceptance walk on a branch the admin denied,
  // re-granting everything below it (a denial down there is not in the deny-list
  // either, because the root above already covered it). The set the checkboxes
  // are drawn from has no such hole: a folder is in the stop-set exactly when
  // the picker showed it ticked.
  const kept = [
    ...new Set(
      denied
        .flatMap((folderId) => descendantIds(folderId, folderIndex))
        .filter((folderId) => !excludedFolderIds.has(folderId))
    ),
  ];

  return {
    projectIds,
    excludedFolderIds: maximalExclusions(denied, folderIndex),
    includedFolderIds: kept,
    projectPermissions: invitePermissionsPayload(offered.catalog, projectPermissions, projectIds),
  };
}

/**
 * The invited projects whose permissions differ from the Member default, as
 * the invite carries them. Undefined when none differ — or when the catalogue
 * is unavailable, since then nothing could have been edited.
 */
function invitePermissionsPayload(
  catalog: PermissionCatalog | null,
  projectPermissions: ReadonlyMap<number, ProjectPermissionSet>,
  projectIds: number[]
): ProjectPermissionsPayload | undefined {
  if (!catalog) return undefined;
  const defaults = presetPermissions("contributor", catalog);
  const changed = projectIds.filter((id) => {
    const set = projectPermissions.get(id);
    return set !== undefined && !samePermissions(set, defaults);
  });
  if (changed.length === 0) return undefined;
  return buildProjectPermissionsPayload(projectPermissions, changed, catalog);
}

function inviteProjectPickerIsLoading(
  isPending: boolean,
  isFetching: boolean,
  hasLoadedData: boolean
): boolean {
  return isPending || (isFetching && !hasLoadedData);
}

function inviteOfferListIsReady(
  projectCount: number | undefined,
  isFetching: boolean,
  isError: boolean
): boolean {
  return Boolean(projectCount) && !isFetching && !isError;
}

/**
 * What send-organization-invite takes. The folder fields ride along on a
 * subcontractor invite only — the function reads them against `projectIds`.
 */
interface InviteRequestBody {
  inviteeEmail: string;
  role: string;
  orgId: string;
  projectIds?: number[];
  excludedFolderIds?: number[];
  includedFolderIds?: number[];
  /** Per-project permission overrides, `{"<project_id>": {"<CODE>": bool}}`. */
  projectPermissions?: ProjectPermissionsPayload;
}

/**
 * Send the invite and report the outcome.
 *
 * The function fails in two shapes — a thrown/transport error, and a 200 whose
 * body carries `error` — and both mean no invitation exists, so both are
 * reported as the same failure rather than one of them passing for a send.
 */
async function sendInviteRequest(
  body: InviteRequestBody,
  messages: { sent: string; failed: string },
  onSent: () => void
): Promise<void> {
  try {
    const { data, error } = await supabase.functions.invoke("send-organization-invite", {
      body,
    });

    if (error) throw error;
    if (data?.error) throw new Error(data.error);

    // One email per send, so the count is always 1; a subcontractor invite is
    // the only project-scoped shape.
    track("invite_sent", {
      role: body.role,
      invite_count: 1,
      scope: body.projectIds?.length ? "project" : "org",
    });
    toast.success(messages.sent);
    onSent();
  } catch (err) {
    if (process.env.NODE_ENV === "development") console.error("Error creating invite:", err);
    toast.error(err instanceof Error ? err.message : messages.failed);
  }
}

export const InviteUserDialog = observer(function InviteUserDialog({
  open,
  onOpenChange,
  onInviteSent,
}: InviteUserDialogProps) {
  const { t } = useLanguage();
  const roleLabel = useRoleLabel();
  const { currentOrganization } = useOrganizationContext();
  const { assignableMemberRoles } = usePermissions();
  const inv = t.settings.invitations;

  const defaultRole = defaultInviteRole(assignableMemberRoles);
  const orgId = currentOrganization?.id;
  const organizationName = currentOrganization?.name;

  const [email, setEmail] = useState("");
  const [role, setRole] = useState<AppRole | "">(defaultRole);
  const [selectedProjectIds, setSelectedProjectIds] = useState<Set<number>>(new Set());
  const [excludedFolderIds, setExcludedFolderIds] = useState<Set<number>>(new Set());
  const [projectPermissions, setProjectPermissions] = useState<
    ReadonlyMap<number, ProjectPermissionSet>
  >(new Map());
  const [isLoading, setIsLoading] = useState(false);

  const isSubcontractor = role === ROLES.SUBCONTRACTOR;

  // Reset the form whenever the dialog opens — and whenever the active org
  // changes, since this dialog is mounted for the lifetime of the settings page
  // and org switching never unmounts it: a selection made in the previous org
  // would otherwise be submitted as foreign project (or folder) ids.
  useEffect(() => {
    if (open) {
      setEmail("");
      setRole(defaultRole);
    }
    setSelectedProjectIds(new Set());
    setExcludedFolderIds(new Set());
    setProjectPermissions(new Map());
  }, [open, orgId, defaultRole]);

  const projectsQuery = useInviteProjects(open && isSubcontractor, orgId);
  const isProjectPickerLoading = inviteProjectPickerIsLoading(
    projectsQuery.isPending,
    projectsQuery.isFetching,
    projectsQuery.data !== undefined
  );

  // The ticks may only be judged against a list fetched for *this* attempt.
  // Reopening the dialog serves the previous generation of the list from cache
  // (gcTime ~5 min) while `refetchOnMount: "always"` refetches, so a list that
  // is merely present is not a list that is current — pruning against the
  // cached one would invite into a project the pending response is about to
  // drop, which is exactly the archived-building access the offer list exists
  // to withhold. The picker keeps rendering the stale rows meanwhile; only
  // sending waits.
  //
  // A failed refetch is the same superseded list wearing a success face: React
  // Query keeps the last `data` and clears `isFetching`, so `isError` has to be
  // read before `data` or the generation the failed refresh would have replaced
  // counts as current.
  //
  // An empty list is current and useless: a subcontractor invite has to carry a
  // project, and the picker already says to go create one. `!!data` is true for
  // `[]`, so without the length check Send stays enabled and answers that copy
  // with "Select at least one project", pointing at a picker with nothing in it.
  const offerListReady = inviteOfferListIsReady(
    projectsQuery.data?.projects.length,
    projectsQuery.isFetching,
    projectsQuery.isError
  );

  const toggleProject = (projectId: number) => {
    setSelectedProjectIds((prev) => {
      const next = new Set(prev);
      if (next.has(projectId)) next.delete(projectId);
      else next.add(projectId);
      return next;
    });
  };

  const folderIndex = useMemo(
    () => buildFolderIndex(projectsQuery.data?.folders ?? []),
    [projectsQuery.data]
  );

  // Both save paths expand every folder they are sent over its own subtree, so
  // the deny-list has to be closed downwards or the server denies the branch the
  // admin deliberately kept. toggleFolderExclusion is what keeps it closed — it
  // cascades onto the subtree when a folder is unticked and releases the
  // ancestors when one is ticked back on. This dialog held its own copy of the
  // ancestor half while the manage dialog spoke deltas; there is one rule now.
  const toggleFolder = (folderId: number) => {
    setExcludedFolderIds((prev) => toggleFolderExclusion(prev, folderId, folderIndex));
  };

  const handleSendInvite = async () => {
    if (!currentOrganization) {
      toast.error(inv.noOrganisation);
      return;
    }
    if (!email.trim() || !email.includes("@")) {
      toast.error(inv.invalidEmail);
      return;
    }
    if (!role) {
      toast.error(inv.chooseRole);
      return;
    }

    const body: InviteRequestBody = {
      inviteeEmail: email.trim(),
      role,
      orgId: currentOrganization.id,
    };

    if (isSubcontractor) {
      const selection = resolveInviteProjectIds(
        projectsQuery,
        folderIndex,
        selectedProjectIds,
        excludedFolderIds,
        projectPermissions,
        t
      );
      if (!("projectIds" in selection)) {
        if (selection.prunedIds) setSelectedProjectIds(new Set(selection.prunedIds));
        toast.error(selection.error);
        return;
      }
      body.projectIds = selection.projectIds;
      body.excludedFolderIds = selection.excludedFolderIds;
      body.includedFolderIds = selection.includedFolderIds;
      if (selection.projectPermissions) body.projectPermissions = selection.projectPermissions;
    }

    setIsLoading(true);
    try {
      await sendInviteRequest(body, { sent: inv.sent, failed: inv.sendFailed }, () => {
        onOpenChange(false);
        onInviteSent?.();
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      {/* A member invite carries the two-pane access editor; other roles are a
          plain form. */}
      <DialogContent
        className={cn(
          "max-h-[calc(100dvh-2rem)] overflow-y-auto",
          isSubcontractor ? "sm:max-w-4xl" : "sm:max-w-lg"
        )}
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Mail className="h-5 w-5" />
            {inv.inviteUser}
          </DialogTitle>
          <DialogDescription>
            {organizationName
              ? inv.inviteDescription.replace("{org}", () => organizationName)
              : inv.inviteDescriptionNoOrganisation}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="invite-email">{inv.emailLabel}</Label>
            <Input
              id="invite-email"
              type="email"
              placeholder={inv.emailPlaceholder}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={isLoading}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="invite-role">{t.settings.roles.role}</Label>
            <Select
              value={role}
              onValueChange={(value) => setRole(value as AppRole)}
              disabled={isLoading || assignableMemberRoles.length === 0}
            >
              <SelectTrigger id="invite-role">
                <SelectValue placeholder={inv.rolePlaceholder} />
              </SelectTrigger>
              <SelectContent>
                {assignableMemberRoles.map((r) => (
                  <SelectItem key={r} value={r}>
                    {roleLabel(r)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {isSubcontractor && (
            <div className="space-y-2">
              <Label>{t.settings.roles.projectsLabel}</Label>
              <InviteProjectPicker
                data={projectsQuery.data}
                selectedProjectIds={selectedProjectIds}
                excludedFolderIds={excludedFolderIds}
                onToggle={toggleProject}
                onToggleFolder={toggleFolder}
                projectPermissions={projectPermissions}
                onChangePermissions={(projectId, next) =>
                  setProjectPermissions((prev) => new Map(prev).set(projectId, next))
                }
                // isPending, not just isFetching: the render where the query is
                // first enabled — and every render while it is disabled — has
                // fetchStatus "idle", and falling through to the picker's empty
                // state would tell an admin with projects to go create one.
                // Only spin while there is nothing to show: the global client
                // leaves refetchOnWindowFocus on, so tabbing away and back
                // refetches, and blanking the list would hide ticks that are
                // still selected and still submittable. `isFetching` still
                // covers the retry after a failed first load, which has data
                // neither loaded nor pending.
                isLoading={isProjectPickerLoading}
                // React Query keeps the last data on a failed refetch. The picker
                // decides from `data` whether the failure replaces the rows
                // (nothing loaded) or sits above them as a retry (a loaded list
                // that could not be refreshed) — blanking rows that are still
                // selected would hide ticks the admin can still act on.
                isError={projectsQuery.isError}
                onRetry={() => void projectsQuery.refetch()}
                isRetrying={projectsQuery.isFetching}
                disabled={isLoading}
              />
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isLoading}>
            {inv.cancel}
          </Button>
          {/* A subcontractor invite carries project ids, so it cannot be judged
              — or sent — before a current offer list is in hand: `isLoading`
              tracks the invite request only, and a click while the list is
              loading, refetching or failed would prune the ticks against
              nothing, or against a generation already superseded. */}
          <Button
            onClick={handleSendInvite}
            disabled={isLoading || (isSubcontractor && !offerListReady)}
          >
            {isLoading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                {inv.sending}
              </>
            ) : (
              inv.send
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
});
